import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, CheckCircle2, Home, Droplet, ChefHat, Box, Shield, Clock } from 'lucide-react';
import { motion } from 'framer-motion';

const HERO_IMAGE = 'https://images.unsplash.com/photo-1758448511322-8bfc73daf606?w=1200&q=85';

export default function ServicesLandingPage() {
  const navigate = useNavigate();

  const services = [
    {
      icon: Home,
      title: 'Rénovation complète',
      desc: 'Transformation totale de A à Z. Nous coordonnons tous les corps de métier.',
      duration: '8-16 semaines',
      path: '/services/renovation-complete',
      badge: 'Plus demandé'
    },
    {
      icon: Droplet,
      title: 'Salle de bain',
      desc: 'Installation complète pilotée. Plomberie, carrelage, équipements.',
      duration: '2-4 semaines',
      path: '/services/salle-de-bain'
    },
    {
      icon: ChefHat,
      title: 'Cuisine',
      desc: 'Pose complète avec coordination cuisiniste, plombier, électricien.',
      duration: '2-4 semaines',
      path: '/services/cuisine'
    },
    {
      icon: Box,
      title: 'Plans 3D',
      desc: 'Visualisation photo-réaliste avant exécution. 2-3 propositions.',
      duration: '1-2 semaines',
      path: '/services/plans-3d',
      badge: 'Optionnel'
    }
  ];

  const pains = [
    '10 artisans qui ne se parlent pas',
    'Un planning qui explose de 3 mois',
    'Vous qui coordonnez tout seul',
    'Des finitions bâclées',
    'Un budget qui dérape',
    'Un résultat décevant'
  ];

  const pillars = [
    {
      icon: Shield,
      title: 'Coordination totale',
      desc: 'Un seul interlocuteur. Nous synchronisons tous les artisans.'
    },
    {
      icon: Clock,
      title: 'Planning tenu',
      desc: 'Dates fixées, jalons clairs, délais respectés. Pas de mauvaise surprise.'
    },
    {
      icon: CheckCircle2,
      title: 'Contrôle qualité',
      desc: 'Suivi hebdomadaire, points de contrôle, réception propre.'
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--hr-bg)' }}>
      
      {/* HERO - PERTINENCE + DÉSIR */}
      <section className="relative overflow-hidden hero-gradient">
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20 lg:pt-24 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 space-y-6">
              <Badge 
                className="rounded-full px-4 py-1.5 font-['Montserrat'] text-xs font-medium uppercase"
                style={{ backgroundColor: 'var(--hr-accent)', color: 'var(--hr-accent-fg)' }}
              >
                Nos services
              </Badge>
              
              <h1 
                className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight"
                style={{ color: 'var(--hr-text)' }}
                data-testid="services-title"
              >
                Des travaux sans chaos
              </h1>
              
              <h2 
                className="text-xl sm:text-2xl leading-relaxed"
                style={{ color: 'var(--hr-text-muted)' }}
              >
                Nous ne faisons pas que des travaux. Nous pilotons votre rénovation de bout en bout.
              </h2>

              <div className="flex gap-4 pt-4">
                <Button
                  size="lg"
                  className="rounded-xl px-8"
                  style={{ backgroundColor: 'var(--hr-primary)', color: 'var(--hr-primary-fg)' }}
                  onClick={() => navigate('/devis')}
                  data-testid="services-hero-cta"
                >
                  Demander un devis
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="media-frame">
                <img src={HERO_IMAGE} alt="Services Homely Rénovation" className="w-full h-auto" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ÉMOTION - Le problème */}
      <section className="py-16 sm:py-20" style={{ backgroundColor: 'var(--hr-bg-alt)' }}>
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-center mb-12" style={{ color: 'var(--hr-text)' }}>
              Pourquoi les rénovations tournent mal
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {pains.map((pain, i) => (
                <motion.div
                  key={i}
                  className="flex items-start gap-3 p-4 rounded-xl"
                  style={{ backgroundColor: 'var(--hr-bg)' }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="mt-1 h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--hr-danger)' }} />
                  <p className="text-base" style={{ color: 'var(--hr-text)' }}>{pain}</p>
                </motion.div>
              ))}
            </div>
            <div className="mt-8 text-center">
              <p className="text-lg font-medium" style={{ color: 'var(--hr-text)' }}>
                Le problème ? <span className="font-semibold">Personne ne coordonne.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CLARTÉ/CONTRÔLE - Notre approche */}
      <section className="py-16 sm:py-20">
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="font-['Montserrat'] text-xs font-medium uppercase tracking-wide mb-4" style={{ color: 'var(--hr-accent)' }}>
              Notre approche
            </p>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold" style={{ color: 'var(--hr-text)' }}>
              Nous pilotons. Vous suivez sereinement.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {pillars.map((pillar, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="rounded-2xl border-[color:var(--hr-border)] h-full">
                  <CardContent className="p-8 space-y-4 text-center">
                    <div className="h-14 w-14 rounded-xl flex items-center justify-center mx-auto" style={{ backgroundColor: 'var(--hr-primary)' }}>
                      <pillar.icon className="h-7 w-7" style={{ color: 'var(--hr-primary-fg)' }} />
                    </div>
                    <h3 className="font-['Figtree'] text-lg font-semibold" style={{ color: 'var(--hr-text)' }}>
                      {pillar.title}
                    </h3>
                    <p className="text-sm leading-relaxed" style={{ color: 'var(--hr-text-muted)' }}>
                      {pillar.desc}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* DÉSIR + IMAGE MENTALE - Nos services */}
      <section className="py-16 sm:py-20" style={{ backgroundColor: 'var(--hr-bg-alt)' }}>
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold mb-4" style={{ color: 'var(--hr-text)' }}>
              Nos services pilotés
            </h2>
            <p className="text-lg" style={{ color: 'var(--hr-text-muted)' }}>
              Chaque service inclut coordination, suivi et contrôle qualité.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <Card 
                  className="rounded-2xl border-[color:var(--hr-border)] h-full hover:shadow-[0_18px_48px_rgba(43,43,43,0.12)] transition-all cursor-pointer group"
                  onClick={() => navigate(service.path)}
                  data-testid={`service-card-${i}`}
                >
                  <CardContent className="p-8 space-y-4">
                    <div className="flex items-start justify-between">
                      <div className="h-12 w-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--hr-bg-alt)' }}>
                        <service.icon className="h-6 w-6" style={{ color: 'var(--hr-primary)' }} />
                      </div>
                      {service.badge && (
                        <Badge 
                          className="rounded-full text-xs"
                          style={{ backgroundColor: 'var(--hr-accent)', color: 'var(--hr-accent-fg)' }}
                        >
                          {service.badge}
                        </Badge>
                      )}
                    </div>
                    <div>
                      <h3 className="font-['Playfair_Display'] text-2xl font-semibold mb-2" style={{ color: 'var(--hr-text)' }}>
                        {service.title}
                      </h3>
                      <p className="text-base leading-relaxed mb-4" style={{ color: 'var(--hr-text-muted)' }}>
                        {service.desc}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium" style={{ color: 'var(--hr-accent)' }}>
                          Durée : {service.duration}
                        </span>
                        <ArrowRight className="h-5 w-5 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" style={{ color: 'var(--hr-primary)' }} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Card className="rounded-2xl border-[color:var(--hr-border)] inline-block">
              <CardContent className="p-8">
                <p className="text-lg font-medium mb-6" style={{ color: 'var(--hr-text)' }}>
                  Besoin d'un accompagnement complet ?
                </p>
                <Button
                  size="lg"
                  className="rounded-xl px-8"
                  style={{ backgroundColor: 'var(--hr-primary)', color: 'var(--hr-primary-fg)' }}
                  onClick={() => navigate('/suivi-de-chantier')}
                >
                  Découvrir notre suivi de chantier
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA FINAL - CONVERSION */}
      <section className="py-20 sm:py-24" style={{ backgroundColor: 'var(--hr-primary)' }}>
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <h2 
            className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-semibold"
            style={{ color: 'var(--hr-primary-fg)' }}
          >
            Prêt à lancer votre projet ?
          </h2>
          <p 
            className="text-lg sm:text-xl leading-relaxed max-w-2xl mx-auto"
            style={{ color: 'rgba(245,243,239,0.9)' }}
          >
            Recevez un devis détaillé et un plan d'action clair. Sans engagement.
          </p>
          <Button
            size="lg"
            className="rounded-xl px-10 py-6 text-lg font-medium"
            style={{ backgroundColor: 'var(--hr-bg)', color: 'var(--hr-text)' }}
            onClick={() => navigate('/devis')}
            data-testid="services-final-cta"
          >
            Demander mon devis cadré
            <ArrowRight className="ml-2 h-6 w-6" />
          </Button>
        </div>
      </section>
    </div>
  );
}
